import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/services/api';
import { postsService, type Post, type PostFilters, type CreatePostDto, type XCreditUsage } from '@/shared/services/posts.service';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';

const POSTS_KEY = 'posts';
/** The approval queue's key (features/composer/components/approval): approving anywhere empties it. */
const APPROVAL_QUEUE_KEY = 'approval-queue';

/** The most posts a range view loads (ten pages of the API's maximum, 100). */
export const RANGE_POST_CAP = 1000;
const RANGE_PAGE_SIZE = 100;

/** Posts in a range, every page of it up to RANGE_POST_CAP; `total` is how many the API has. */
export interface PostsInRange {
  data: Post[];
  pagination: { total: number; page: number; limit: number };
}

/**
 * Every post matching `filters` (a calendar or overview range), read page by
 * page until the API's total is reached or RANGE_POST_CAP. One page of 100
 * silently dropped the rest of a busy month. `data.length < total` means the
 * cap was hit, and the caller says so.
 */
export async function listAllPosts(filters: PostFilters, cap: number = RANGE_POST_CAP): Promise<PostsInRange> {
  const data: Post[] = [];
  let total = 0;
  for (let page = 1; data.length < cap; page++) {
    const res = await postsService.list({ ...filters, page, limit: RANGE_PAGE_SIZE });
    data.push(...res.data);
    total = res.pagination.total;
    if (res.data.length < RANGE_PAGE_SIZE || data.length >= total) break;
  }
  const kept = data.slice(0, cap);
  return { data: kept, pagination: { total: Math.max(total, kept.length), page: 1, limit: kept.length } };
}

/** usePosts for a range view: all pages, not the first 100. */
export function usePostsInRange(filters: Omit<PostFilters, 'page' | 'limit'>) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [POSTS_KEY, 'range', filters, wsId],
    queryFn: () => listAllPosts(filters),
    enabled: !!wsId,
    // A range is many pages: not read again on every window focus (PC-31).
    staleTime: 30_000,
  });
}

/**
 * Moves a post to a new time on the calendar at once (PC-31): the cached
 * ranges show it in its new place while the server saves, and go back when
 * the server refuses; everything is read again once it has answered.
 */
export function useReschedulePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scheduledAt }: { id: string; scheduledAt: string }) => postsService.update(id, { scheduledAt }),
    onMutate: async ({ id, scheduledAt }) => {
      await queryClient.cancelQueries({ queryKey: [POSTS_KEY, 'range'] });
      const before = queryClient.getQueriesData<PostsInRange>({ queryKey: [POSTS_KEY, 'range'] });
      queryClient.setQueriesData<PostsInRange>({ queryKey: [POSTS_KEY, 'range'] }, (old) =>
        old ? { ...old, data: old.data.map((p) => (p.id === id ? { ...p, scheduledAt } : p)) } : old,
      );
      return { before };
    },
    onError: (_err, _vars, context) => {
      for (const [key, data] of context?.before ?? []) queryClient.setQueryData(key, data);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [POSTS_KEY] });
    },
  });
}

export function usePosts(filters: PostFilters = {}, opts?: { refetchInterval?: number | false }) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [POSTS_KEY, filters, wsId],
    queryFn: () => postsService.list(filters),
    enabled: !!wsId,
    refetchInterval: opts?.refetchInterval,
  });
}

/**
 * The account's X post credits for the month a post would publish in: the
 * composer's counter. Keyed by month, so picking another day of the same
 * month does not refetch. `at` null is now.
 */
export function useXCredits(opts: { enabled: boolean; at: string | null; postId: string | null }) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const when = opts.at ? new Date(opts.at) : null;
  const month = when && !Number.isNaN(when.getTime()) ? `${when.getFullYear()}-${when.getMonth() + 1}` : 'now';
  return useQuery<XCreditUsage>({
    queryKey: [POSTS_KEY, 'x-credits', wsId, month, opts.postId],
    queryFn: () => postsService.xCredits(opts.at, opts.postId),
    enabled: opts.enabled && !!wsId,
    staleTime: 30_000,
  });
}

export function usePost(id: string | undefined) {
  return useQuery({
    queryKey: [POSTS_KEY, id],
    queryFn: () => postsService.getById(id!),
    enabled: !!id,
  });
}

export function useCreatePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreatePostDto) => postsService.create(dto),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSTS_KEY] });
    },
  });
}

export function useUpdatePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Partial<CreatePostDto> }) =>
      postsService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSTS_KEY] });
    },
  });
}

export function useDeletePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => postsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSTS_KEY] });
    },
  });
}

export function usePublishPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => postsService.publish(id),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSTS_KEY] });
    },
  });
}

export function useSubmitForApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => postsService.submitForApproval(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSTS_KEY] });
    },
  });
}

/** POST /posts/:id/approve — approve a post awaiting approval (blueprint M14). */
export function useApprovePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, publishImmediately = false, scheduledAt }: { id: string; publishImmediately?: boolean; scheduledAt?: string }): Promise<Post> => {
      if (scheduledAt === undefined) return postsService.approve(id, publishImmediately);
      // A new time chosen while approving (the post's own has passed).
      const res = await api.post(`/posts/${id}/approve`, { publishImmediately, scheduledAt });
      return res.data as Post;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [APPROVAL_QUEUE_KEY] });
    },
  });
}

/**
 * POST /posts/:id/retry — publish a FAILED or PARTIAL post again, to its
 * failed channels only. Publish (DRAFT/SCHEDULED only) refused these.
 */
export function useRetryPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<Post> => {
      const res = await api.post(`/posts/${id}/retry`);
      return res.data as Post;
    },
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSTS_KEY] });
    },
  });
}

/**
 * Duplicate a post as a new unscheduled draft (blueprint C08 / M14), through
 * POST /posts/:id/clone: caption, channels, post types, media and each
 * channel's options are copied on the server, so nothing is dropped.
 */
export function useDuplicatePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<Post> => {
      const res = await api.post(`/posts/${id}/clone`);
      return res.data as Post;
    },
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [POSTS_KEY] });
    },
  });
}
